import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search } from 'lucide-react';
import SwapAvatar from './SwapAvatar';
import { SwapColleague } from '../../../../api/Attendance/shiftSwap';

interface ColleaguePickerProps {
  colleagues: SwapColleague[];
  value: string;
  onChange: (id: string) => void;
  loading?: boolean;
  disabled?: boolean;
  testId?: string;
}

// Searchable people list with avatars. Replaces the name-only dropdown so a colleague is
// recognisable by face, and the whole (company-wide) list stays scannable via search.
const ColleaguePicker: React.FC<ColleaguePickerProps> = ({ colleagues, value, onChange, loading, disabled, testId }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const q = search.trim().toLowerCase();
  const filtered = q === '' ? colleagues : colleagues.filter((c) => (c.fullName || c.id).toLowerCase().includes(q));
  const selectedIndex = filtered.findIndex((c) => c.id === value);

  if (loading) {
    return (
      <div className="space-y-2" data-testid={testId}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 animate-pulse rounded-lg border-2 border-border bg-muted/40" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <div className="relative mb-2">
        <Search size={15} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('Search colleagues')}
          aria-label={t('Search colleagues')}
          disabled={disabled}
          className="h-9 w-full rounded-lg border border-border bg-background ps-9 pe-3 text-sm text-foreground placeholder-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          {colleagues.length === 0 ? t('No colleagues available to swap with') : t('No colleagues match your search')}
        </p>
      ) : (
        <div className="max-h-56 space-y-1.5 overflow-y-auto pe-1" role="radiogroup" aria-label={t('Select a colleague')}>
          {filtered.map((c, i) => {
            const selected = c.id === value;
            return (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                tabIndex={i === (selectedIndex >= 0 ? selectedIndex : 0) ? 0 : -1}
                ref={(el) => { optionRefs.current[i] = el; }}
                onClick={() => onChange(c.id)}
                onKeyDown={(e) => {
                  const move = (to: number) => {
                    const n = filtered.length;
                    if (n === 0) return;
                    const next = ((to % n) + n) % n;
                    onChange(filtered[next].id);
                    optionRefs.current[next]?.focus();
                  };
                  switch (e.key) {
                    case 'ArrowDown':
                    case 'ArrowRight':
                      e.preventDefault();
                      move(i + 1);
                      break;
                    case 'ArrowUp':
                    case 'ArrowLeft':
                      e.preventDefault();
                      move(i - 1);
                      break;
                    case 'Home':
                      e.preventDefault();
                      move(0);
                      break;
                    case 'End':
                      e.preventDefault();
                      move(filtered.length - 1);
                      break;
                    default:
                      break;
                  }
                }}
                data-testid={`colleague-option-${c.id}`}
                className={`flex w-full items-center gap-3 rounded-lg border-2 px-3 py-2 text-start transition-colors disabled:opacity-50 ${
                  selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                }`}
              >
                <SwapAvatar name={c.fullName || c.id} imageUrl={c.imageUrl} className="h-8 w-8" />
                <span className={`min-w-0 flex-1 truncate text-sm font-medium ${selected ? 'text-primary' : 'text-foreground'}`}>
                  {c.fullName || c.id}
                </span>
                {selected && <Check size={16} className="shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ColleaguePicker;
